import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Calendar, MessageSquare, CheckCircle, XCircle, Clock, Eye, EyeOff } from 'lucide-react';
import axios from '@services/api/axios.instance';
import { toast } from 'react-hot-toast';

const QuestionDetails = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuestionDetails();
  }, [questionId]);

  const fetchQuestionDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/admin/questions/${questionId}`);
      
      if (response.data.success) {
        setQuestion(response.data.data.question);
        
        if (!response.data.data.question.adminReadFlag) {
          await markAsRead();
        }
      }
    } catch (error) {
      console.error('Error fetching question details:', error);
      toast.error('Failed to fetch question details');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await axios.put(`/admin/questions/${questionId}/mark-read`);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const toggleReadStatus = async () => {
    try {
      if (question.adminReadFlag) {
        await axios.put(`/admin/questions/${questionId}/mark-unread`);
        toast.success('Marked as unread');
      } else {
        await axios.put(`/admin/questions/${questionId}/mark-read`);
        toast.success('Marked as read');
      }
      fetchQuestionDetails();
    } catch (error) {
      console.error('Error toggling read status:', error);
      toast.error('Failed to update read status');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, label: 'Pending' },
      answered: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: CheckCircle, label: 'Answered' },
      approved: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Question Not Found</h2>
          <button
            onClick={() => navigate('/admin/questions')}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Questions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="w-full">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/questions')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Questions</span>
          </button>

          <button
            onClick={toggleReadStatus}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              question.adminReadFlag
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {question.adminReadFlag ? (
              <>
                <EyeOff className="w-4 h-4" />
                Mark as Unread
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Mark as Read
              </>
            )}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="bg-[#1a2642] px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-white">Question Details</h1>
              {getStatusBadge(question.status)}
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Student Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {question.student?.profilePicture ? (
                      <img
                        src={question.student.profilePicture}
                        alt={question.studentName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                        {question.studentName?.charAt(0) || 'S'}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{question.studentName}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {question.student?.email}
                      </p>
                      {question.student?.phone && (
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {question.student.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Mentor Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {question.mentor?.profilePicture ? (
                      <img
                        src={question.mentor.profilePicture}
                        alt={question.mentorName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold text-lg">
                        {question.mentorName?.charAt(0) || 'M'}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{question.mentorName}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {question.mentor?.email}
                      </p>
                      {question.mentor?.currentPosition && (
                        <p className="text-sm text-gray-600">{question.mentor.currentPosition}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Question</h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap">{question.question}</p>
                  <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Asked on {formatDate(question.questionDate)}
                    </span>
                    {question.credits > 0 && (
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                        {question.credits} Credits
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {question.answer && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Answer</h3>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-gray-800 whitespace-pre-wrap">{question.answer}</p>
                    <div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Answered on {formatDate(question.answerDate)}
                      </span>
                      {question.rating && (
                        <span className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          Rating: {question.rating}/5
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {question.adminRemarks && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Admin Remarks</h3>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-gray-800">{question.adminRemarks}</p>
                    {question.adminApprover && (
                      <p className="text-sm text-gray-600 mt-2">
                        By: {question.adminApprover.firstName} {question.adminApprover.lastName}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-sm text-gray-600">Admin Read Status</p>
                  <p className="font-semibold text-gray-900">
                    {question.adminReadFlag ? (
                      <span className="text-green-600">✓ Read</span>
                    ) : (
                      <span className="text-orange-600">Unread</span>
                    )}
                  </p>
                  {question.adminReadAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(question.adminReadAt)}
                    </p>
                  )}
                </div>

                {question.adminApprovalDate && (
                  <div>
                    <p className="text-sm text-gray-600">Approval Date</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(question.adminApprovalDate)}
                    </p>
                  </div>
                )}

                {question.ratingDate && (
                  <div>
                    <p className="text-sm text-gray-600">Rated On</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(question.ratingDate)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetails;
